import React from 'react'
import { Space } from 'antd'
import type { RefSelectProps } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import Select from '../../Select.jsx'
import Button from '../../Button.jsx'
import type { FilterConfig, FilterLocalState, FilterOptions, FilterSelectedKey } from '../TableViewTypes'

interface MultiselectFilterDropdownProps {
  columnKey: string
  columnTitle?: string
  actualFilterKey: string
  filterLocalState: FilterLocalState
  setFilterLocalState: React.Dispatch<React.SetStateAction<FilterLocalState>>
  selectedKeys: FilterSelectedKey[]
  setSelectedKeys: (selectedKeys: FilterSelectedKey[]) => void
  confirm: () => void
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  onFiltersChange?: (filters: FilterConfig) => void
}

export default function MultiselectFilterDropdown({
  columnKey,
  columnTitle,
  actualFilterKey,
  filterLocalState,
  setFilterLocalState,
  selectedKeys,
  setSelectedKeys,
  confirm,
  filterOptions,
  filterOptionsLoading,
  onFiltersChange
}: MultiselectFilterDropdownProps) {
  const localValue = filterLocalState[columnKey]?.multiselect
  const currentValue = localValue !== undefined ? localValue : selectedKeys || []
  const hasSelections = currentValue && currentValue.length > 0

  if (localValue !== undefined && JSON.stringify(selectedKeys || []) !== JSON.stringify(localValue)) {
    setSelectedKeys(localValue || [])
  }

  const handleApply = () => {
    if (hasSelections) {
      setFilterLocalState((prev) => {
        const newState = { ...prev }
        delete newState[columnKey]
        return newState
      })
      confirm()
    }
  }

  const handleClear = () => {
    setFilterLocalState((prev) => {
      const newState = { ...prev }
      delete newState[columnKey]
      return newState
    })

    setSelectedKeys([])

    const filterConfig: FilterConfig = {}
    filterConfig[actualFilterKey] = undefined
    onFiltersChange?.(filterConfig)

    confirm()
  }

  return (
    <div style={{ padding: 8, width: 250 }}>
      <Select
        mode='multiple'
        placeholder={`Select ${columnTitle}`}
        value={currentValue}
        onChange={(values: string[]) => {
          setFilterLocalState((prev) => ({
            ...prev,
            [columnKey]: { ...prev[columnKey], multiselect: values }
          }))
          setSelectedKeys(values)
        }}
        style={{ width: '100%', marginBottom: 8 }}
        options={
          filterOptions?.[actualFilterKey]?.map((option) =>
            typeof option === 'string'
              ? { label: option, value: option }
              : { label: option.label || option.name || option, value: option.value || option }
          ) || []
        }
        loading={filterOptionsLoading}
        showSearch
        autoFocus
        filterOption={(input: string, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase())}
        ref={(select: RefSelectProps | null) => {
          if (select) {
            setTimeout(() => {
              ;(select as any).focus()
            }, 100)
          }
        }}
      />
      <Space>
        <Button variant='primary' size='small' onClick={handleApply} disabled={!hasSelections}>
          Filter
        </Button>
        <Button variant='cancel' size='small' onClick={handleClear}>
          Clear
        </Button>
      </Space>
    </div>
  )
}

export function MultiselectFilterIcon(filtered: boolean) {
  return <FontAwesomeIcon icon={faFilter} style={{ color: filtered ? '#1890ff' : '#9CA3AF', padding: '0 4px' }} />
}
